import React, {useEffect, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {scroller} from "react-scroll";
import classes from "./Navigation.module.scss";
import HoverBlueBorderLink from "../reusable-components/hover-blue-border-link/HoverBlueBorderLink.tsx";
import MobileNav from "./MobileNav.tsx";

import arrowBottomIconGray from "../../media/navigation/arrow-bottom-gray.svg";
import arrowBottomIconBlue from "../../media/navigation/arrow-bottom-blue.svg";
import phoneIcon from "../../media/navigation/phone.svg";
import logo from "../../media/logo.svg";
import logoSmall from "../../media/logo-small.svg";
import menuIcon from "../../media/icons/menu.svg";
import cyberTrackerLogo from "../../media/case-study/cybertracker-logo.svg";
import eroadLogo from "../../media/case-study/eroad-card.webp";
import echerhaLogo from "../../media/case-study/echerha-logo.svg";
import mayorsOfficeLogo from "../../media/case-study/1551-logo.svg";
import syviLogo from '../../routes/projects/syvi/media/logo.svg'
import goGoBagLogo from '../../routes/projects/go-go-bag/media/logo.svg'
import { useTranslation } from "react-i18next";

interface NavigationLink {
    title: string;
    withDropDown: boolean;
    id: number;
    link?: string;
    dropdown?:
        | Array<{ title: string; link: string }>
        | Array<{ title: string; link: string; text: string; img: string }>
        | Array<Array<{ title: string; link: string }>>
        | {
        left: Array<{ title: string; link: string; item: string }>;
        right: {
            [key: string]: Array<{ title: string; link: string }>;
        };
    };
}


export const SCROLL_CONFIG = {
    smooth: true,
    offset: -50,
    duration: 800,
};

const Navigation: React.FC = () => {
    const [selectedMenuItemId, setSelectedMenuItemId] = useState<number | null>(null);
    const [hoveredMenuItemId, setHoveredMenuItemId] = useState<number | null>(null);
    const [navigationSideElementsHidden, setNavigationSideElementsHidden] = useState(false);
    const [navColor, setNavColor] = useState<"light" | "dark">("dark");
    const [navIsOpened, setNavIsOpened] = useState(false);
    const [mobileNavIsOpened, setMobileNavIsOpened] = useState(false);
    const [selectedServicesMenuItem, setSelectedServicesMenuItem] = useState("it-consulting");
    const [phoneNumberShown, setPhoneNumberShown] = useState(false);
    const navRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const NAVIGATION_LINKS: NavigationLink[] = [
        {
            title: t('header.companyTitle'),
            withDropDown: true,
            id: 1,
            dropdown: t('header.companyLinks', {returnObjects: true})
        },
        {
            title: t('header.service.title'),
            withDropDown: true,
            id: 2,
            dropdown: {
                left: t('header.service.dropdownLeft', {returnObjects: true}),
                right: {
                    "it-consulting": t('header.service.dropdownRight.it-consulting', {returnObjects: true}),
                    "software-development": t('header.service.dropdownRight.software-development', {returnObjects: true}),
                    "implementation-support": t('header.service.dropdownRight.implementation-support', {returnObjects: true}),
                    "startup-development": t('header.service.dropdownRight.startup-development', {returnObjects: true})
                },
            },
        },
        {
            title: t('header.projectsTitle'),
            withDropDown: true,
            id: 3,
            dropdown: [
                {
                title: t('header.projectsDropdown.0.title'),
                text: t('header.projectsDropdown.0.text'),
                link: "/case-study/cybertracker",
                img: cyberTrackerLogo,
                },
                {
                title: t('header.projectsDropdown.1.title'),
                text: t('header.projectsDropdown.1.text'),
                link: "/case-study/e-road",
                img: eroadLogo,
                },
                {
                title: t('header.projectsDropdown.2.title'),
                text: t('header.projectsDropdown.2.text'),
                link: "/case-study/e-cherha",
                img: echerhaLogo,
                },
                {
                title: t('header.projectsDropdown.3.title'),
                text: t('header.projectsDropdown.3.text'),
                link: "/case-study/1551",
                img: mayorsOfficeLogo,
                },
                {
                title: t('header.projectsDropdown.4.title'),
                text: t('header.projectsDropdown.4.text'),
                link: "/case-study/syvi",
                img: syviLogo,
                },
                {
                title: t('header.projectsDropdown.5.title'),
                text: t('header.projectsDropdown.5.text'),
                link: "/case-study/go-go-bag",
                img: goGoBagLogo,
                },
            ],
        },
        {
            title: t('header.industriesTitle'),
            withDropDown: true,
            id: 4,
            dropdown: [
                [
                    {
                        title: t('header.industriesLinks.0.title'),
                        link: "/industries/finance-and-banking"
                    },
                    {
                        title: t('header.industriesLinks.1.title'),
                        link: "/industries/telecommunications"
                    },
                    {
                        title: t('header.industriesLinks.2.title'),
                        link: "/industries/life-insurance"
                    },
                    {
                        title: t('header.industriesLinks.3.title'),
                        link: "/industries/public-sectors"
                    }
                ],
                [
                    {
                        title: t('header.industriesLinks.4.title'),
                        link: "/industries/road-engineering"
                    },
                    {
                        title: t('header.industriesLinks.5.title'),
                        link: "/industries/healthcare"
                    },
                    {
                        title: t('header.industriesLinks.6.title'),
                        link: "/industries/startup-product-engineering-expertise"
                    },
                    {
                        title: t('header.industriesLinks.7.title'),
                        link: "/industries/real-estate"
                    }
                ],
                [
                    {
                        title: t('header.industriesLinks.8.title'),
                        link: "/industries/logistics-and-supply-chains"
                    },
                    {
                        title: t('header.industriesLinks.9.title'),
                        link: "/industries/e-commerce"
                    },
                    {
                        title: t('header.industriesLinks.10.title'),
                        link: "/industries/retail"
                    },
                    {
                        title: t('header.industriesLinks.11.title'),
                        link: "/industries/horeca"
                    }
                ]
            ],
        },
        {
            title: t('header.solutionsTitle'),
            withDropDown: true,
            id: 5,
            dropdown: [
                [
                    {title: t('header.solutionsLinks.0.title'), link: "/solutions/smart-warehouse-management"},
                    {title: t('header.solutionsLinks.1.title'), link: "/solutions/project-management-system"},
                    {title: t('header.solutionsLinks.2.title'), link: "/solutions/lims-solutions"},
                    {title: t('header.solutionsLinks.3.title'), link: "/solutions/accounting-and-billing-systems-development"},
                    {title: t('header.solutionsLinks.4.title'), link: "/solutions/procurement-software-solution"},
                    {title: t('header.solutionsLinks.5.title'), link: "/solutions/erp-for-pharma"},
                    {title: t('header.solutionsLinks.6.title'), link: "/solutions/erp-for-logistics"},
                ],
                [
                    {title: t('header.solutionsLinks.7.title'), link: "/solutions/erp-for-utility-companies"},
                    {title: t('header.solutionsLinks.8.title'), link: "/solutions/fleet-management-system"},
                    {title: t('header.solutionsLinks.9.title'), link: "/solutions/scada-solutions"},
                    {title: t('header.solutionsLinks.10.title'), link: "/solutions/road-condition-monitoring-systems"},
                    {title: t('header.solutionsLinks.11.title'), link: "/solutions/iot-solution-development-services"},
                    {title: t('header.solutionsLinks.12.title'), link: "/solutions/route-planning-system"},
                    {title: t('header.solutionsLinks.13.title'), link: "/solutions/bi-systems"},
                ],
                [
                    {title: t('header.solutionsLinks.14.title'), link: "/solutions/cargo-tracking-system"},
                    {title: t('header.solutionsLinks.15.title'), link: "/solutions/crm-for-logistics"},
                    {title: t('header.solutionsLinks.16.title'), link: "/solutions/ehr-emr-software-development"},
                    {title: t('header.solutionsLinks.17.title'), link: "/solutions/production-process-monitoring-systems"},
                    {title: t('header.solutionsLinks.18.title'), link: "/solutions/crm-for-energy-sector"},
                    {title: t('header.solutionsLinks.19.title'), link: "/solutions/erp-for-financial-systems"},
                    {title: t('header.solutionsLinks.20.title'), link: "/solutions/road-machinery-control-systems"},
                ]
            ]

        },
        {title: t('header.blog'), link: "/blogs", withDropDown: false, id: 6},
    ];

    const handleScroll = () => {
        const sections = document.querySelectorAll("section");
        let currentSection = "nav-light";
        setNavigationSideElementsHidden(window.scrollY > 50);

        sections.forEach((section) => {
            if (section.getBoundingClientRect().top <= 0) {
                currentSection = section.id;
            }
        });

        setNavColor(
            currentSection === "nav-light" ? "light" : currentSection === "section-two" ? "dark" : "dark",
        );
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (navRef.current && !navRef.current.contains(event.target as Node)) {
            setSelectedMenuItemId(null);
        }
    };

    const handleNavigationItemClick = (link: NavigationLink) => {
        setSelectedMenuItemId((prev) => (prev === link.id ? null : link.id));
        if (link.link) {
            navigate("/blogs");
        }
        setNavIsOpened(true);
    };

    const handleContactUs = () => {
        const contactElement = document.getElementById("contact");
        if (contactElement) {
            scroller.scrollTo("contact", SCROLL_CONFIG);
        } else {
            navigate("/");
            setTimeout(() => {
                scroller.scrollTo("contact", SCROLL_CONFIG);
            }, 100);
        }
    };

    const handlePhoneClick = () => {
        setPhoneNumberShown((prev) => !prev);
    }


    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handleScrollReset = () => setSelectedMenuItemId(null);
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("scroll", handleScrollReset);
        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("scroll", handleScrollReset);
        };
    }, []);


    const renderCompanyDropdown = () => (
        <div
            className={`${classes["navigation-dropdown"]} ${classes["navigation-dropdown--company"]} ${
                selectedMenuItemId === 1 ? classes.visible : classes.hidden
            }`}
        >
            {NAVIGATION_LINKS.find((link) => link.id === 1)?.dropdown?.map(
                ({title, link}: { title: string; link: string }) => (
                    <HoverBlueBorderLink key={title} link={link} onClick={() => setNavIsOpened(false)}>
                        {title}
                    </HoverBlueBorderLink>
                ),
            )}
        </div>
    );

    const renderServicesDropdown = () => (
        <div
            className={`${classes["navigation-dropdown"]} ${classes["navigation-dropdown--services"]} ${
                selectedMenuItemId === 2 ? classes.visible : classes.hidden
            }`}
        >
            <div className={classes.left}>
                {(NAVIGATION_LINKS.find((link) => link.id === 2)?.dropdown as any)?.left.map(
                    ({link, title, item}: { link: string; title: string; item: string }) => (
                        <HoverBlueBorderLink
                            key={title}
                            link={link}
                            onClick={() => setSelectedServicesMenuItem(item)}
                            isCurrentlySelected={selectedServicesMenuItem === item}
                        >
                            {title}
                        </HoverBlueBorderLink>
                    ),
                )}
            </div>
            <div className={classes.divider}></div>
            <div className={classes.right}>
                <div className={classes.content}>
                    <div className={classes["links-container"]}>
                        {(NAVIGATION_LINKS.find((link) => link.id === 2)?.dropdown as any)?.right[
                            selectedServicesMenuItem
                            ]?.map(({title, link}: { title: string; link: string }) => (
                            <Link
                                className={classes.link}
                                key={title}
                                to={link}
                                onClick={() => {
                                    setSelectedServicesMenuItem("it-consulting");
                                    setNavIsOpened(false);
                                }}
                            >
                                {title}
                            </Link>
                        ))}
                    </div>
                    <Link
                        to={"/services"}
                        className={classes["view-all"]}
                        onClick={() => {
                            setSelectedServicesMenuItem("it-consulting");
                            setNavIsOpened(false);
                        }}
                    >
                        {t('header.viewAll')}
                    </Link>
                </div>
            </div>
        </div>
    );

    const renderProjectsDropdown = () => (
        <div
            className={`${classes["navigation-dropdown"]} ${classes["navigation-dropdown-projects"]} ${
                selectedMenuItemId === 3 ? classes.visible : classes.hidden
            }`}
        >
            {(NAVIGATION_LINKS.find((link) => link.id === 3)?.dropdown as any)?.map(
                ({title, link, text, img}: { title: string; link: string; text: string; img: string }) => (
                    <HoverBlueBorderLink
                        link={link}
                        key={title}
                        linkClassName={classes["project-container"]}
                        linkWrapClassName={classes.wrap}
                        onClick={() => setNavIsOpened(false)}
                    >
                        <div className={classes["img-container"]}>
                            <img src={img} className={classes["img"]} alt={title}/>
                        </div>
                        <div className={classes["text-container"]}>
                            <h4 className={classes.title}>{title}</h4>
                            <p className={classes.text}>{text}</p>
                        </div>
                    </HoverBlueBorderLink>
                ),
            )}
        </div>
    );

    const renderIndustriesDropdown = () => (
        <div
            className={`${classes["navigation-dropdown"]} ${classes["navigation-dropdown-fields"]} ${
                selectedMenuItemId === 4 ? classes.visible : classes.hidden
            }`}
        >
            <div className={classes["links-container"]}>
                {(NAVIGATION_LINKS.find((link) => link.id === 4)?.dropdown as any)?.map(
                    (column: Array<{ title: string; link: string }>, index: number) => (
                        <div key={`column-${index}`} className={classes["links-column"]}>
                            {column.map(({link, title}) => (
                                <Link
                                    className={classes.link}
                                    to={link}
                                    key={title}
                                    onClick={() => setNavIsOpened(false)}
                                >
                                    {title}
                                </Link>
                            ))}
                        </div>
                    ),
                )}
            </div>
            <Link to={"/services"} className={classes["view-all"]} onClick={() => setNavIsOpened(false)}>
                {t('header.viewAll')}
            </Link>
        </div>
    );

    const renderSolutionsDropdown = () => (
        <div
            className={`${classes["navigation-dropdown"]} ${classes["navigation-dropdown-solutions"]} ${
                selectedMenuItemId === 5 ? classes.visible : classes.hidden
            }`}
        >
            <div className={classes["links-container"]}>
                {(NAVIGATION_LINKS.find((link) => link.id === 5)?.dropdown as any)?.map(
                    (column: Array<{ title: string; link: string }>, index: number) => (
                        <div key={`column-${index}`} className={classes["links-column"]}>
                            {column.map(({link, title}) => (
                                <Link className={classes.link} to={link} key={title}
                                      onClick={() => setNavIsOpened(false)}>
                                    {title}
                                </Link>
                            ))}
                        </div>
                    ),
                )}
            </div>
            <Link to={"/solutions"} className={classes["view-all"]} onClick={() => setNavIsOpened(false)}>
                {t('header.viewAll')}
            </Link>
        </div>
    );
    
    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    return (
        <div className={classes["navigation-container"]} ref={navRef}>
            <nav className={`${classes.navigation} ${classes["navigation--desktop"]}`}>
                <Link to={"/"} onClick={() => setNavIsOpened(false)}>
                    <img
                        src={logo}
                        alt="Logo"
                        className={`${classes.logo} ${navigationSideElementsHidden && classes.hidden}`}
                    />
                </Link>
                <div className={`${classes["navigation-block"]}`}>
                    <div className={`${classes["navigation-items-container"]} ${classes[navColor]}`}>
                        <Link
                            onClick={() => setNavIsOpened(false)}
                            to={"/"}
                            className={`${classes["logo-small-container"]} ${
                                !navigationSideElementsHidden ? classes["logo-small-container--hidden"] : ""
                            }`}
                        >
                            <img src={logoSmall} alt="Logo" className={classes["logo-small"]}/>
                        </Link>
                        {NAVIGATION_LINKS.map((link) => (
                            <button
                                key={link.id}
                                onMouseEnter={() => setHoveredMenuItemId(link.id)}
                                onMouseLeave={() => setHoveredMenuItemId(null)}
                                onClick={() => handleNavigationItemClick(link)}
                                className={`${classes["navigation-item-wrap"]} ${
                                    selectedMenuItemId === link.id ? classes["navigation-item-wrap--selected"] : ""
                                }`}
                            >
                                <div className={classes["navigation-item-container"]}>
                                    <span className={classes["navigation-item-text"]}>{link.title}</span>
                                    {link.withDropDown && (
                                        <img
                                            src={
                                                hoveredMenuItemId === link.id || selectedMenuItemId === link.id
                                                    ? arrowBottomIconBlue
                                                    : arrowBottomIconGray
                                            }
                                            alt="Arrow bottom"
                                        />
                                    )}
                                </div>
                            </button>
                        ))}
                        <div className={classes["contact-buttons-container"]}>
                            <div className={classes["phone-button-wrap"]}>
                                <button
                                    className={`${classes["phone-button"]} ${phoneNumberShown && classes["phone-button--show-number"]}`}
                                    onClick={handlePhoneClick}>
                                    {!phoneNumberShown && (
                                        <img alt="Phone" src={phoneIcon} className={classes["phone-icon"]}/>
                                    )}
                                    {phoneNumberShown &&
                                        <a href="tel:+14151234567" className={classes["phone-number"]}>+1 (415)
                                            123-4567</a>}
                                </button>
                            </div>
                            <button className={classes["contact-button"]} onClick={handleContactUs}>
                                {t('header.contactUs')}
                            </button>
                        </div>
                        
                    </div>
                    {navIsOpened && (
                        <>
                            {renderCompanyDropdown()}
                            {renderServicesDropdown()}
                            {renderProjectsDropdown()}
                            {renderIndustriesDropdown()}
                            {renderSolutionsDropdown()}
                        </>
                    )}
                    
                </div>
                <div
                    className={`${classes["language-toggle"]} ${navigationSideElementsHidden && classes.hidden}`}
                        >
                    <button
                    className={classes["language-toggle--item-wrap"]}
                    onClick={() => changeLanguage("ua")}
                    >
                    <span
                        className={`${classes["language-toggle--item"]} ${currentLanguage === "ua" ? classes.selected : classes.unselected}`}
                    >
                        UA
                    </span>
                    </button>
        
                    <button
                    className={classes["language-toggle--item-wrap"]}
                    onClick={() => changeLanguage("eng")}
                    >
                    <span
                        className={`${classes["language-toggle--item"]} ${currentLanguage === "eng" ? classes.selected : classes.unselected}`}
                    >
                        ENG
                    </span>
                    </button>
                </div>
            </nav>
            <nav className={classes["navigation--mobile"]}>
                <div className={classes["logo-container"]}>
                    <Link to={"/"} onClick={() => setNavIsOpened(false)}>
                        <img
                            src={logo}
                            alt="Logo"
                            className={`${classes.logo} ${navigationSideElementsHidden && classes.hidden}`}
                        />
                    </Link>

                    <div
                        className={`${classes["language-toggle"]} ${navigationSideElementsHidden && classes.hidden}`}
                            >
                        <button
                        className={classes["language-toggle--item-wrap"]}
                        onClick={() => changeLanguage("ua")}
                        >
                        <span
                            className={`${classes["language-toggle--item"]} ${currentLanguage === "ua" ? classes.selected : classes.unselected}`}
                        >
                            UA
                        </span>
                        </button>
            
                        <button
                        className={classes["language-toggle--item-wrap"]}
                        onClick={() => changeLanguage("eng")}
                        >
                        <span
                            className={`${classes["language-toggle--item"]} ${currentLanguage === "eng" ? classes.selected : classes.unselected}`}
                        >
                            ENG
                        </span>
                        </button>
                    <button
                        className={`${classes["menu-button"]} ${navigationSideElementsHidden && classes.hidden}`}
                        onClick={() => setMobileNavIsOpened(true)}
                        >
                        <img src={menuIcon} alt="Menu"/>
                    </button>
                        </div>
                </div>
            </nav>
            <MobileNav
                mobileNavIsOpened={mobileNavIsOpened}
                classes={classes}
                setMobileNavIsOpened={setMobileNavIsOpened}
            />
        </div>
    );
};

export default Navigation;